import { removeMyReplenishmentProduct } from "packages/membership/api/favourites/remove";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import type { ReorderProductsArgs } from "../order-it-again.api";
import { getOrderItAgainProducts } from "../order-it-again.api";
import { useTrackElementClicked } from "packages/membership/tracking";
import type { BaseTrackingValues } from "packages/membership/tracking/types";
import type { OIASortBy, OIATimePeriod } from "@shared/graphql.types";
import { navigate } from "utils/routes";
import { usePathname } from "next/navigation";

type FavouritesType = "order_it_again" | "wishlist";

type UseFavouritesArgs = {
  currentPage: number;
  type: FavouritesType;
};

type FavouritesViewModel = {
  clearMessages: () => void;
  timePeriod: OIATimePeriod;
  errorMsg: string;
  handleCategoryChange: (value: string) => void;
  handleReset: () => void;
  handleSortChange: (option: string) => void;
  handleTimePeriodChange: (option: string) => void;
  isLoading: boolean;
  onProductRemoveClicked: (sku: string) => void;
  reorderProducts: ReorderProductsArgs;
  setReorderProducts: React.Dispatch<React.SetStateAction<ReorderProductsArgs>>;
  selectedCategories: string[];
  showReset: boolean;
  sort: OIASortBy;
  successMsg: string;
};

const STORAGE_KEYS = {
  CATEGORIES: "favourites-categories",
  SORT: "favourites-sort",
  PERIOD: "favourites-period"
} as const;

const storage = {
  get: (key: string) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (e) {
      console.error("Error reading from localStorage:", e);
      return null;
    }
  },
  set: (key: string, value: unknown) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error("Error writing to localStorage:", e);
    }
  },
  remove: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error("Error removing from localStorage:", e);
    }
  }
};

const useResetStorageOnLeave = () => {
  const pathname = usePathname();

  useEffect(() => {
    const currentPath = pathname;

    const handleBeforeUnload = () => {
      const targetUrl =
        document.activeElement instanceof HTMLAnchorElement
          ? document.activeElement.pathname
          : "";

      // Only clear if we're navigating to a different pathname
      if (
        currentPath === "/account/favourites/order-it-again" &&
        targetUrl &&
        targetUrl !== "/account/favourites/order-it-again"
      ) {
        try {
          window.localStorage.removeItem(STORAGE_KEYS.CATEGORIES);
          window.localStorage.removeItem(STORAGE_KEYS.SORT);
          window.localStorage.removeItem(STORAGE_KEYS.PERIOD);
        } catch (error) {
          console.error("Storage access error:", error);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [pathname]);
};

export const useFavourites = ({
  currentPage,
  type
}: UseFavouritesArgs): FavouritesViewModel => {
  useResetStorageOnLeave();
  // state
  const [isLoading, setIsLoading] = useState(true);

  const [reorderProducts, setReorderProducts] = useState<ReorderProductsArgs>({
    title: "",
    subtitle: "",
    products: [],
    pagination: {
      currentPage: 1,
      perPage: 32,
      total: 0
    },
    showOrderItAgain: true
  });

  // Categories state and handlers
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    return storage.get(STORAGE_KEYS.CATEGORIES) || [];
  });

  const handleCategoryChange = (value: string) => {
    const isSelected = selectedCategories.includes(value);
    setSelectedCategories((prev) => {
      const newCategories = isSelected
        ? prev.filter((category) => category !== value)
        : [...prev, value];
      storage.set(STORAGE_KEYS.CATEGORIES, newCategories);
      return newCategories;
    });

    elementClicked({
      details: isSelected ? "category_removed" : "category_added",
      details2: value
    });

    navigate("/account/favourites/order-it-again?page=1");
  };

  // Sort state and handlers
  const [sort, setSort] = useState<OIASortBy>(() => {
    const savedSort = storage.get(STORAGE_KEYS.SORT);
    return savedSort ? (savedSort as OIASortBy) : "RECOMMENDED";
  });

  const handleSortChange = (option: string) => {
    setSort(option as OIASortBy);
    elementClicked({
      details: "sort_changed",
      details2: option
    });
  };

  useEffect(() => {
    storage.set(STORAGE_KEYS.SORT, sort);
  }, [sort]);

  // Time period state and handlers
  const [timePeriod, setTimePeriod] = useState<OIATimePeriod>(() => {
    const savedPeriod = storage.get(STORAGE_KEYS.PERIOD);
    return savedPeriod ? (savedPeriod as OIATimePeriod) : "ALL_TIME";
  });

  const handleTimePeriodChange = (option: string) => {
    setTimePeriod(option as OIATimePeriod);
    elementClicked({
      details: "duration_changed",
      details2: option
    });
  };

  useEffect(() => {
    storage.set(STORAGE_KEYS.PERIOD, timePeriod);
  }, [timePeriod]);

  // Messages state
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const fetchReorderProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getOrderItAgainProducts({
        filteringOptions: {
          categories: selectedCategories
        },
        pagination: {
          currentPage,
          perPage: 32
        },
        sortOptions: sort,
        period: timePeriod
      });
      setReorderProducts(response);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, selectedCategories, sort, timePeriod]);

  useEffect(() => {
    fetchReorderProducts();
  }, [fetchReorderProducts]);

  useEffect(() => {
    if (!reorderProducts.showOrderItAgain) {
      navigate("/account/order-history");
    }
  }, [reorderProducts.showOrderItAgain]);

  // Reset button state and handlers
  const [showReset, setShowReset] = useState(false);

  const handleReset = () => {
    setSelectedCategories([]);
    setSort("RECOMMENDED");
    setTimePeriod("ALL_TIME");

    storage.remove(STORAGE_KEYS.CATEGORIES);
    storage.remove(STORAGE_KEYS.SORT);
    storage.remove(STORAGE_KEYS.PERIOD);

    elementClicked({
      details: "filters_reset"
    });

    navigate("/account/favourites/order-it-again?page=1");
  };

  useEffect(() => {
    setShowReset(
      selectedCategories.length > 0 ||
        sort !== "RECOMMENDED" ||
        timePeriod !== "ALL_TIME"
    );
  }, [selectedCategories, sort, timePeriod, currentPage]);

  // Tracking setup
  const baseTrackingValues: BaseTrackingValues = {
    target: `favourites_${type}`,
    location: `favourites_${type}`
  };
  const { elementClicked } = useTrackElementClicked(baseTrackingValues);

  const clearMessages = () => {
    setErrorMsg("");
    setSuccessMsg("");
  };

  // Remove product handlers
  const handleRemoveReplenishmentProduct = async (sku: string) => {
    try {
      await removeMyReplenishmentProduct(sku);
      setSuccessMsg(
        "Successfully removed product, you won't see this again unless you reorder it"
      );
    } catch (error) {
      setErrorMsg("Something went wrong");
    }
  };

  const onProductRemoveClicked = (sku: string) => {
    setReorderProducts((prevState) => ({
      ...prevState,
      products: prevState.products.filter((p) => p.variants[0].sku !== sku)
    }));
    handleRemoveReplenishmentProduct(sku);
    elementClicked({
      details: "remove",
      details2: sku
    });
  };

  return {
    clearMessages,
    timePeriod,
    errorMsg,
    handleCategoryChange,
    handleReset,
    handleSortChange,
    handleTimePeriodChange,
    isLoading,
    onProductRemoveClicked,
    reorderProducts,
    setReorderProducts,
    selectedCategories,
    showReset,
    sort,
    successMsg
  };
};
